import { useMemo } from "react";

import { useFormikWithDirtyForm } from "@shared";
import { useAlertContext } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as Yup from "yup";

import { GoalResponse, clientsApi } from "@/api";
import { useDropdownOptions } from "@/common/hooks/useDropdownOptions";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

export interface GoalsResultsRowValues {
  goalId?: string;
  achievementDate: Date | null;
  goalResult: string;
}

export interface GoalsResultsFormValues {
  rows: GoalsResultsRowValues[];
}

export function useGoalsResults(clientId: string) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showSuccess } = useAlertContext();

  const { data: savedGoals = [] } = useQuery<GoalResponse[]>({
    queryKey: [QueryKey.ClientGoals, clientId],
    queryFn: () => clientsApi.getGoalsResultsByClientId({ clientId }),
    meta: { loadingScope: "pageContent" },
  });

  const { clientGoalsAndResults: goalOptionStrings } = useDropdownOptions();

  const syncMutation = useMutation({
    mutationFn: (rows: GoalsResultsRowValues[]) => {
      const validRows = rows.filter(
        (r): r is GoalsResultsRowValues & { achievementDate: Date } => r.achievementDate !== null
      );
      return clientsApi.updateClientGoalsResults({
        clientId,
        updateClientGoalsRequest: {
          goals: validRows.map((r) => ({
            id: r.goalId ?? null,
            goalResult: r.goalResult,
            achievementDate: r.achievementDate,
          })),
        },
      });
    },
  });

  const save = async (rows: GoalsResultsRowValues[]) => {
    await syncMutation.mutateAsync(rows);
    showSuccess(t("clients.goalsResults.saveSuccess"));
    void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientGoals, clientId] });
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        rows: Yup.array().of(
          Yup.object().shape({
            achievementDate: Yup.date()
              .typeError(t("common.validation.invalidDateFormat"))
              .required(t("common.validation.fieldRequired", { fieldName: t("clients.goalsResults.achievementDate") })),
            goalResult: Yup.string().required(
              t("common.validation.fieldRequired", { fieldName: t("clients.goalsResults.goalResult") })
            ),
          })
        ),
      }),
    [t]
  );

  const formik = useFormikWithDirtyForm<GoalsResultsFormValues>({
    initialValues: {
      rows: savedGoals.map((g) => ({
        goalId: g.id,
        achievementDate: g.achievementDate,
        goalResult: g.goalResult,
      })),
    },
    enableReinitialize: true,
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      await save(values.rows);
    },
  });

  const handleAddRow = () => {
    void formik.setFieldValue("rows", [...formik.values.rows, { achievementDate: null, goalResult: "" }]);
  };

  const handleDeleteRow = (index: number) => {
    void formik.setFieldValue(
      "rows",
      formik.values.rows.filter((_, i) => i !== index)
    );
  };

  return {
    formik,
    goalOptionStrings,
    handleAddRow,
    handleDeleteRow,
  };
}
